import { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

export function MultiSelectDropdown({
  value = [],
  onChange,
  options,
  placeholder,
  className = '',
  disabled = false,
  loading = false,
  grouped = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const handleToggle = () => {
    if (disabled || loading) return;
    setIsOpen(!isOpen);
  };

  const handleSelect = (optionValue) => {
    if (disabled || loading) return;

    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];

    onChange(newValue);
  };

  const handleRemove = (optionValue, e) => {
    e.stopPropagation();
    if (disabled || loading) return;
    onChange(value.filter(v => v !== optionValue));
  };

  const handleClearAll = (e) => {
    e.stopPropagation();
    if (disabled || loading) return;
    onChange([]);
  };

  // Filter options based on search
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group options by category if grouped prop is true
  const groupedOptions = grouped
    ? filteredOptions.reduce((acc, option) => {
        const category = option.category || 'Other';
        if (!acc[category]) acc[category] = [];
        acc[category].push(option);
        return acc;
      }, {})
    : { '': filteredOptions };

  // Get selected option labels
  const selectedLabels = value.map(val => {
    const option = options.find(opt => opt.value === val);
    return option ? option.label : val;
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className='relative' ref={dropdownRef}>
      <div
        onClick={handleToggle}
        className={`w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-left text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-slate-300 transition-colors cursor-pointer ${
          disabled || loading ? 'opacity-50 cursor-not-allowed' : ''
        } ${className}`}
      >
        <div className='flex items-center justify-between gap-2'>
          <div className='flex-1 flex flex-wrap gap-1.5'>
            {value.length === 0 ? (
              <span className='text-slate-400'>{placeholder}</span>
            ) : (
              <>
                {selectedLabels.slice(0, 2).map((label, index) => (
                  <span
                    key={value[index]}
                    className='inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium'
                  >
                    {label}
                    <button
                      onClick={(e) => handleRemove(value[index], e)}
                      className='hover:text-blue-900'
                    >
                      <XMarkIcon className='h-3 w-3' />
                    </button>
                  </span>
                ))}
                {value.length > 2 && (
                  <span className='inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium'>
                    +{value.length - 2} more
                  </span>
                )}
              </>
            )}
          </div>
          <div className='flex items-center gap-2'>
            {value.length > 0 && !disabled && !loading && (
              <button
                onClick={handleClearAll}
                className='text-gray-400 hover:text-gray-600'
              >
                <XMarkIcon className='h-4 w-4' />
              </button>
            )}
            {loading ? (
              <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
            ) : (
              <ChevronDownIcon
                className={`w-4 h-4 text-slate-400 transition-transform ${
                  isOpen ? 'rotate-180' : ''
                }`}
              />
            )}
          </div>
        </div>
      </div>

      {isOpen && !loading && !disabled && (
        <div className='absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-80 overflow-hidden flex flex-col'>
          <div className='p-2 border-b border-slate-200'>
            <input
              type='text'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder='Search...'
              className='w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Options List */}
          <div className='overflow-y-auto flex-1'>
            {Object.keys(groupedOptions).length === 0 ? (
              <div className='px-4 py-3 text-sm text-gray-500 text-center'>
                No options found
              </div>
            ) : (
              Object.entries(groupedOptions).map(([category, categoryOptions]) => (
                <div key={category}>
                  {grouped && category && (
                    <div className='px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 uppercase tracking-wider'>
                      {category}
                    </div>
                  )}
                  {categoryOptions.map((option) => {
                    const isSelected = value.includes(option.value);
                    return (
                      <div
                        key={option.value}
                        onClick={() => handleSelect(option.value)}
                        className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-blue-50 text-blue-700 font-medium'
                            : 'text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        <div className='flex items-center justify-between'>
                          <span>{option.label}</span>
                          {isSelected && (
                            <div className='w-4 h-4 bg-blue-600 rounded-sm flex items-center justify-center'>
                              <svg
                                className='w-3 h-3 text-white'
                                fill='none'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth='2'
                                viewBox='0 0 24 24'
                                stroke='currentColor'
                              >
                                <path d='M5 13l4 4L19 7' />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {value.length > 0 && (
            <div className='p-2 border-t border-slate-200 bg-slate-50 text-xs text-slate-600'>
              {value.length} selected
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MultiSelectDropdown;
